import { createClient } from "@supabase/supabase-js";

// ─── Supabase client ──────────────────────────────────────────────────────────
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "https://uzdhysqurkbenrbwxpae.supabase.co";
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InV6ZGh5c3F1cmtiZW5yYnd4cGFlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODIwNTY3MjEsImV4cCI6MjA5NzYzMjcyMX0.T0ILFO-z-aL_4dpEkgNxoOd8m6ytU3dIMViszsiW4rg";
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

// ─── Field normaliser ─────────────────────────────────────────────────────────
// Supabase uses snake_case (is_coming_soon, is_featured). The frontend
// components were built with camelCase (coming_soon, featured). Normalise
// once here so nothing else needs to change.
function normalise(p) {
  if (!p) return p;
  const stock = p.stock ?? 0;
  const threshold = p.low_stock_threshold ?? 5;
  const manual = p.availability || "available";
  // sold out if manually set OR stock hits zero (and not a preorder)
  const isSoldOut = manual === "sold_out" || (manual !== "preorder" && stock <= 0 && !p.is_coming_soon);
  const isPreorder = manual === "preorder";
  // "Few stitches left" — only when genuinely low but still in stock, and not preorder/sold out
  const lowStock = !isSoldOut && !isPreorder && stock > 0 && stock <= threshold;
  let availabilityState = "available";
  if (isSoldOut) availabilityState = "sold_out";
  else if (isPreorder) availabilityState = "preorder";
  return {
    ...p,
    coming_soon: p.is_coming_soon ?? p.coming_soon ?? false,
    featured:    p.is_featured    ?? p.featured    ?? false,
    image:       (p.images && p.images[0]) || p.image || null,
    rating:      p.rating      ?? 0,
    review_count: p.review_count ?? 0,
    stock,
    availabilityState,   // 'available' | 'preorder' | 'sold_out'
    lowStock,            // boolean → show "Few stitches left"
  };
}

// ─── FALLBACK (used if Supabase is unreachable) ───────────────────────────────
const FALLBACK_PRODUCTS = [
  {
    id: "p1", slug: "superior-anarchyx", name: "Superior Anarchyx",
    price: 1099, sale_price: 799, category: "tshirts", tag: "DROP 01",
    featured: true, coming_soon: false,
    sizes: ["S","M","L","XL","XXL"], sizes_out_of_stock: [], colors: ["scarlet"],
    stock: 50, rating: 0, review_count: 0,
    images: [], image: null,
    description: "Some heroes wear hope. Others wear evolution.",
    story: "For the ones who know heroes aren't always polished.",
    features: ["220 GSM Bio-Washed Cotton","Oversized Drop-Shoulder Fit","Contrasted Black Ribbed Collar","Laser-Etched Screen Print"],
    care: ["Machine wash cold, inside out","Do not bleach","Tumble dry low or hang dry"],
  },
  {
    id: "p3", slug: "jojo", name: "Coming Soon",
    price: 0, sale_price: null, category: "tshirts", tag: "SOON",
    featured: false, coming_soon: true,
    sizes: [], sizes_out_of_stock: [], colors: [], stock: 0,
    rating: 0, review_count: 0, images: [], image: null,
    description: "Next drop loading.", story: "", features: [], care: [],
  },
];

// ─── Products ─────────────────────────────────────────────────────────────────
export async function fetchProducts(params = {}) {
  try {
    let q = supabase.from("products").select("*").eq("is_active", true);
    if (params.category && params.category !== "all") q = q.eq("category", params.category);
    if (params.exclude) q = q.neq("id", params.exclude);
    if (params.sort === "price-asc")  q = q.order("price", { ascending: true });
    else if (params.sort === "price-desc") q = q.order("price", { ascending: false });
    else q = q.order("created_at", { ascending: true });
    if (params.limit) q = q.limit(params.limit);
    const { data, error } = await q;
    if (error) throw error;
    return data.map(normalise);
  } catch {
    let list = [...FALLBACK_PRODUCTS];
    if (params.category && params.category !== "all") list = list.filter(p => p.category === params.category);
    if (params.exclude) list = list.filter(p => p.id !== params.exclude);
    if (params.sort === "price-asc") list.sort((a, b) => a.price - b.price);
    else if (params.sort === "price-desc") list.sort((a, b) => b.price - a.price);
    if (params.limit) list = list.slice(0, params.limit);
    return list;
  }
}

export async function fetchFeatured() {
  try {
    const { data, error } = await supabase
      .from("products").select("*")
      .eq("is_active", true).eq("is_featured", true)
      .order("created_at", { ascending: true });
    if (error) throw error;
    return data.map(normalise);
  } catch {
    return FALLBACK_PRODUCTS.filter(p => p.featured);
  }
}

export async function fetchProduct(idOrSlug) {
  try {
    // Try slug first, then UUID
    const isUUID = /^[0-9a-f-]{36}$/i.test(idOrSlug);
    const field  = isUUID ? "id" : "slug";
    const { data, error } = await supabase
      .from("products").select("*")
      .eq(field, idOrSlug).maybeSingle();
    if (error) throw error;
    if (!data) {
      // Fallback: try the other field
      const { data: d2 } = await supabase
        .from("products").select("*")
        .eq(isUUID ? "slug" : "id", idOrSlug).maybeSingle();
      if (!d2) throw new Error("not found");
      return normalise(d2);
    }
    return normalise(data);
  } catch {
    return normalise(FALLBACK_PRODUCTS.find(p => p.id === idOrSlug || p.slug === idOrSlug) || FALLBACK_PRODUCTS[0]);
  }
}

export async function fetchSimilar(category, excludeId) {
  return fetchProducts({ category, exclude: excludeId, limit: 4 });
}

// ─── Orders ───────────────────────────────────────────────────────────────────
export async function createOrder(payload) {
  const num = `MT-${String(Math.floor(Math.random() * 900000) + 100000)}`;
  try {
    const { data, error } = await supabase.from("orders").insert([{
      order_number:     num,
      customer_name:    payload.delivery?.name    || "Guest",
      customer_email:   payload.delivery?.email   || "",
      customer_phone:   payload.delivery?.phone   || null,
      shipping_address: payload.delivery?.address || "",
      shipping_city:    payload.delivery?.city    || null,
      shipping_state:   payload.delivery?.state   || null,
      shipping_pincode: payload.delivery?.pincode || null,
      subtotal:         payload.subtotal    || 0,
      promo_code:       payload.promo_code  || null,
      promo_discount:   payload.promo_discount || 0,
      delivery_fee:     payload.delivery_fee   || 0,
      total:            payload.total       || 0,
      status:           "pending",
      payment_method:   payload.payment_method || "razorpay",
    }]).select().single();
    if (error) throw error;
    // Insert line items
    if (payload.items?.length) {
      const items = payload.items.map(i => ({
        order_id:     data.id,
        product_id:   i.product_id || null,
        product_name: i.name  || "Unknown",
        size:         i.size  || null,
        quantity:     i.qty   || 1,
        price:        i.price || 0,
      }));
      await supabase.from("order_items").insert(items);
    }
    return { ...data, order_number: num };
  } catch {
    // Offline fallback — still show a success screen
    return { order_number: num, ...payload, mock: true };
  }
}

export async function fetchOrder(orderNumber) {
  try {
    const { data, error } = await supabase
      .from("orders").select("*, order_items(*)")
      .eq("order_number", orderNumber).maybeSingle();
    if (error) throw error;
    return data || { order_number: orderNumber, items: [], total: 0 };
  } catch {
    return { order_number: orderNumber, items: [], total: 0 };
  }
}

// ─── Promo codes ──────────────────────────────────────────────────────────────
export async function fetchPromoCode(code) {
  if (!code) return null;
  try {
    const { data, error } = await supabase
      .from("promo_codes").select("*")
      .eq("code", code.trim().toUpperCase())
      .eq("is_active", true)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    // Check expiry if set
    if (data.expires_at && new Date(data.expires_at) < new Date()) return null;
    // Check max uses if set
    if (data.max_uses != null && data.used_count >= data.max_uses) return null;
    return { code: data.code, type: data.type, value: data.value, label: data.label, min_order: data.min_order || 0, min_quantity: data.min_quantity || 0, waives_shipping: !!data.waives_shipping, max_per_customer: data.max_per_customer || null, buy_qty: data.buy_qty || null, get_qty: data.get_qty || null };
  } catch {
    return null;
  }
}

// Fetch the shipping rate for a given state. Falls back to the configurable
// default_shipping_rate setting if the state isn't listed.
export async function fetchShippingRate(state) {
  const fallback = Number(await getSetting("default_shipping_rate", "119")) || 119;
  if (!state) return fallback;
  try {
    const { data, error } = await supabase
      .from("shipping_rates").select("rate")
      .ilike("state", state.trim())
      .maybeSingle();
    if (error || !data) return fallback;
    return data.rate;
  } catch {
    return fallback;
  }
}

// Returns the codes of all currently active, unexpired promo codes.
// Used to display available codes at checkout so the list is never stale.
export async function fetchActivePromoCodes() {
  try {
    const { data, error } = await supabase
      .from("promo_codes").select("code, expires_at, max_uses, used_count")
      .eq("is_active", true);
    if (error || !data) return [];
    const now = new Date();
    return data
      .filter((p) => !p.expires_at || new Date(p.expires_at) > now)
      .filter((p) => p.max_uses == null || (p.used_count || 0) < p.max_uses)
      .map((p) => p.code);
  } catch {
    return [];
  }
}

// Look up the state (and city where available) for a 6-digit Indian PIN code.
// Checks the local pincode_states table first (3-digit prefix, then 2-digit),
// falling back to a free public API only if the PIN isn't mapped locally.
export async function lookupPincode(pin) {
  const clean = (pin || "").trim();
  if (!/^\d{6}$/.test(clean)) return null;

  try {
    const { data } = await supabase
      .from("pincode_states").select("prefix, state")
      .in("prefix", [clean.slice(0, 3), clean.slice(0, 2)]);
    if (data && data.length > 0) {
      // Prefer the more specific 3-digit match
      const best = data.sort((a, b) => b.prefix.length - a.prefix.length)[0];
      return { state: best.state, city: null };
    }
  } catch { /* fall through to API */ }

  // Fallback — free public API, used only for unmapped PINs
  try {
    const res = await fetch(`https://api.postalpincode.in/pincode/${clean}`);
    const json = await res.json();
    const po = json?.[0]?.PostOffice?.[0];
    if (!po) return null;
    const titleCase = (s) => (s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    return { state: titleCase(po.State), city: titleCase(po.District) };
  } catch {
    return null;
  }
}
// All configurable copy and thresholds live in the site_settings table so they
// can be changed in Supabase without rebuilding the site.
// ─── Site settings ────────────────────────────────────────────────────────────
// All configurable copy and thresholds live in the site_settings table so they
// can be changed in Supabase without rebuilding the site.
let _settingsCache = null;

export async function fetchSettings() {
  if (_settingsCache) return _settingsCache;
  try {
    const { data, error } = await supabase.from("site_settings").select("key, value");
    if (error || !data) return {};
    _settingsCache = Object.fromEntries(data.map((r) => [r.key, r.value]));
    return _settingsCache;
  } catch {
    return {};
  }
}

export async function getSetting(key, fallback = "") {
  const s = await fetchSettings();
  return s[key] !== undefined && s[key] !== null && s[key] !== "" ? s[key] : fallback;
}

// Returns the shipping price range as display text, e.g. "₹49–₹119".
// Reads shipping_range_label; if that's "auto", computes it from shipping_rates.
export async function fetchShippingRangeLabel() {
  const custom = await getSetting("shipping_range_label", "auto");
  if (custom && custom !== "auto") return custom;
  try {
    const { data, error } = await supabase.from("shipping_rates").select("rate");
    if (error || !data || data.length === 0) return "";
    const rates = data.map((r) => r.rate).filter((n) => typeof n === "number");
    if (rates.length === 0) return "";
    const min = Math.min(...rates);
    const max = Math.max(...rates);
    return min === max ? `₹${min}` : `₹${min}–₹${max}`;
  } catch {
    return "";
  }
}

// Count how many PAID orders a given email has used a specific promo code on.
// Used to enforce per-customer usage limits (e.g. THREADS: max 3 uses).
export async function countPromoUsageByEmail(email, code) {
  if (!email || !code) return 0;
  try {
    const { count, error } = await supabase
      .from("orders")
      .select("id", { count: "exact", head: true })
      .eq("customer_email", email.trim().toLowerCase())
      .eq("promo_code", code.trim().toUpperCase())
      .eq("status", "paid");
    if (error) return 0;
    return count || 0;
  } catch {
    return 0;
  }
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export async function signUpEmail({ email, password, name }) {
  const { data, error } = await supabase.auth.signUp({
    email, password,
    options: { data: { full_name: name || "" } },
  });
  if (error) throw error;
  return data;
}
export async function signInEmail({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}
export async function signInGoogle() {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: `${window.location.origin}/profile` },
  });
  if (error) throw error;
  return data;
}
export async function signOut() { await supabase.auth.signOut(); }
export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data?.session || null;
}
export function onAuthChange(callback) {
  const { data } = supabase.auth.onAuthStateChange((_e, session) => callback(session));
  return () => data?.subscription?.unsubscribe?.();
}

// ─── Profile ──────────────────────────────────────────────────────────────────
export async function getMyProfile() {
  const session = await getSession();
  if (!session?.user) return null;
  const { data, error } = await supabase.from("profiles").select("*").eq("id", session.user.id).maybeSingle();
  if (error) return null;
  return data;
}
export async function checkUsernameAvailable(username) {
  const clean = (username || "").trim().toLowerCase();
  if (!clean || clean.length < 3) return { ok: false, reason: "Username must be at least 3 characters" };
  if (!/^[a-z0-9_]+$/.test(clean)) return { ok: false, reason: "Only letters, numbers, and underscores" };
  const session = await getSession();
  const { data } = await supabase.from("profiles").select("id").eq("username", clean).maybeSingle();
  if (data && data.id !== session?.user?.id) return { ok: false, reason: "Username already taken" };
  return { ok: true };
}
export async function updateUsername(username) {
  const session = await getSession();
  if (!session?.user) throw new Error("Not signed in");
  const clean = username.trim().toLowerCase();
  const check = await checkUsernameAvailable(clean);
  if (!check.ok) throw new Error(check.reason);
  const { data, error } = await supabase.from("profiles").update({ username: clean, updated_at: new Date().toISOString() }).eq("id", session.user.id).select().single();
  if (error) throw error;
  return data;
}
export async function updateDisplayName(displayName) {
  const session = await getSession();
  if (!session?.user) throw new Error("Not signed in");
  const { data, error } = await supabase.from("profiles").update({ display_name: displayName, updated_at: new Date().toISOString() }).eq("id", session.user.id).select().single();
  if (error) throw error;
  return data;
}

// ─── Reviews ──────────────────────────────────────────────────────────────────
export async function fetchReviews(productId) {
  try {
    let q = supabase.from("reviews").select("*").eq("is_approved", true).order("created_at", { ascending: false });
    if (productId) q = q.eq("product_id", productId);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  } catch { return []; }
}
export async function submitReview({ productId, rating, body }) {
  const session = await getSession();
  if (!session?.user) throw new Error("Please sign in to leave a review");
  const profile = await getMyProfile();
  const authorName = profile?.display_name || profile?.username || "Member";
  const { data, error } = await supabase.from("reviews").insert([{
    product_id: productId || null, user_id: session.user.id, author_name: authorName, rating, body,
  }]).select().single();
  if (error) throw error;
  return data;
}

// ─── Razorpay payments ────────────────────────────────────────────────────────
const FN_BASE = `${SUPABASE_URL}/functions/v1`;
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}
async function createRazorpayOrder({ amount, receipt }) {
  const res = await fetch(`${FN_BASE}/create-order`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON}` },
    body: JSON.stringify({ amount: Math.round(amount * 100), currency: "INR", receipt }),
  });
  if (!res.ok) throw new Error("Could not start payment");
  return res.json();
}
async function verifyRazorpayPayment(payload) {
  const res = await fetch(`${FN_BASE}/verify-payment`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON}` },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  return res.ok && data.verified === true;
}
export async function payWithRazorpay({ amountINR, orderNumber, customer }) {
  const ok = await loadRazorpayScript();
  if (!ok) throw new Error("Could not load payment. Check your connection.");
  const order = await createRazorpayOrder({ amount: amountINR, receipt: orderNumber });
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
      name: "Million Threads", description: `Order ${orderNumber}`,
      prefill: { name: customer?.name || "", email: customer?.email || "", contact: customer?.phone || "" },
      theme: { color: "#B81D1D" },
      handler: async (response) => {
        const verified = await verifyRazorpayPayment({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
          order_number: orderNumber,
        });
        if (verified) resolve({ success: true, paymentId: response.razorpay_payment_id });
        else reject(new Error("Payment could not be verified. If money was deducted, contact support."));
      },
      modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
    });
    rzp.on("payment.failed", (resp) => reject(new Error(resp?.error?.description || "Payment failed")));
    rzp.open();
  });
}

// ─── Waitlist (sold-out demand capture) ───────────────────────────────────────
export async function joinWaitlist({ productId, productName, name, email, phone }) {
  try {
    const session = await getSession();
    const { error } = await supabase.from("waitlist").insert([{
      product_id: productId || null,
      product_name: productName || null,
      user_id: session?.user?.id || null,
      email: email || session?.user?.email || null,
      name: name || null,
      phone: phone || null,
    }]);
    if (error) throw error;
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}

// ─── Testimonials (global Thread Log) ─────────────────────────────────────────
export async function fetchTestimonials() {
  try {
    const { data, error } = await supabase
      .from("testimonials").select("*")
      .eq("is_approved", true)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data || [];
  } catch { return []; }
}
export async function submitTestimonial({ authorName, body, location }) {
  try {
    const { error } = await supabase.from("testimonials").insert([{
      author_name: authorName || "Anonymous",
      body, location: location || null,
    }]);
    if (error) throw error;
    return { ok: true };
  } catch (e) { return { ok: false, error: e.message }; }
}

// ─── Contact / messages ───────────────────────────────────────────────────────
export async function sendContact(payload) {
  try {
    const { error } = await supabase.from("messages").insert([{
      name: payload.name || "Anonymous",
      email: payload.email || "",
      message: payload.message || "",
    }]);
    if (error) throw error;
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}

// ─── Profile / Contact (no auth backend yet) ─────────────────────────────────
export async function fetchProfile() {
  return { name: "", email: "", member_since: "", orders: [] };
}
